// SPDX-License-Identifier: GPL-3.0-or-later
// The small meshes of the boss: the shield bubble, the wind-up disc on the table, and the tint
// slots of the boss materials. Each mesh is local: none is bigger than the boss or the targets.
// The colours come from the palette (design tokens).

import {
  Box3,
  CircleGeometry,
  type Color,
  DoubleSide,
  Group,
  type Material,
  Mesh,
  MeshBasicMaterial,
  type MeshStandardMaterial,
  type Object3D,
  RingGeometry,
  SphereGeometry,
  Vector3,
} from "three";
import { colorOf, TOKEN_HEX } from "../vfx/palette.ts";

/** Opacity of the shield bubble at rest. */
export const SHIELD_OPACITY = 0.2;
/** Radius of the wind-up disc, in table units. It covers the four DENY targets. */
export const DISC_RADIUS = 115;
/** The wind-up disc lies this far above the playfield. This stops z fighting. */
const DISC_LIFT = 1.2;

/** The size of a boss model, in scene units. */
export interface BossSize {
  readonly width: number;
  readonly height: number;
  readonly depth: number;
}

/** The size of a model. The matrices must be current. */
export function sizeOf(model: Object3D): BossSize {
  model.updateMatrixWorld(true);
  const size = new Box3().setFromObject(model).getSize(new Vector3());
  return { width: size.x, height: size.y, depth: size.z };
}

/**
 * The shield bubble. It is a white ellipsoid that is a little bigger than the boss. It stands at
 * the middle of the boss height. It starts hidden.
 */
export function makeShield(size: BossSize): Mesh {
  const material = new MeshBasicMaterial({
    color: colorOf(TOKEN_HEX.white),
    transparent: true,
    opacity: SHIELD_OPACITY,
    depthWrite: false,
    side: DoubleSide,
  });
  const mesh = new Mesh(new SphereGeometry(1, 24, 16), material);
  mesh.name = "BossShield";
  mesh.scale.set(size.width * 0.62, size.height * 0.58, Math.max(size.depth, size.width * 0.5));
  mesh.position.y = size.height * 0.5;
  mesh.visible = false;
  mesh.renderOrder = 5;
  return mesh;
}

/** The wind-up disc of the table. `fill` grows with the progress. `ring` is the fixed edge. */
export interface Telegraph {
  readonly group: Group;
  readonly fill: Mesh;
  readonly ring: Mesh;
}

function flat(mesh: Mesh, name: string): Mesh {
  mesh.name = name;
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = DISC_LIFT;
  mesh.visible = false;
  mesh.renderOrder = 4;
  return mesh;
}

/** Make the wind-up disc. It lies flat on the playfield and starts hidden. */
export function makeTelegraph(): Telegraph {
  const group = new Group();
  group.name = "BossTelegraph";
  const fillMaterial = new MeshBasicMaterial({
    color: colorOf(TOKEN_HEX.brand),
    transparent: true,
    opacity: 0.28,
    depthWrite: false,
  });
  const ringMaterial = new MeshBasicMaterial({
    color: colorOf(TOKEN_HEX.white),
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
  });
  const fill = flat(
    new Mesh(new CircleGeometry(DISC_RADIUS, 40), fillMaterial),
    "BossTelegraphFill",
  );
  const ring = flat(
    new Mesh(new RingGeometry(DISC_RADIUS - 3, DISC_RADIUS, 48), ringMaterial),
    "BossTelegraphRing",
  );
  group.add(fill, ring);
  return { group, fill, ring };
}

/** A material that can glow, with the glow colour it had at the start. */
export interface TintSlot {
  readonly material: MeshStandardMaterial;
  readonly base: Color;
}

function isGlowing(material: Material): material is MeshStandardMaterial {
  return (material as MeshStandardMaterial).isMeshStandardMaterial === true;
}

/** The materials of a model that take a tint. */
export function tintSlots(model: Object3D): TintSlot[] {
  const slots: TintSlot[] = [];
  model.traverse((obj) => {
    const material = (obj as Mesh).material as Material | Material[] | undefined;
    if (material === undefined) return;
    for (const m of Array.isArray(material) ? material : [material]) {
      if (isGlowing(m)) slots.push({ material: m, base: m.emissive.clone() });
    }
  });
  return slots;
}

/** Mix the glow of each slot toward a colour. `amount` 0 gives the start colour back. */
export function applyTint(slots: readonly TintSlot[], hex: number, amount: number): void {
  const target = colorOf(hex);
  for (const slot of slots) slot.material.emissive.copy(slot.base).lerp(target, amount);
}

/** A material that can fade, with the values it had at the start. */
export interface FadeSlot {
  readonly material: Material;
  readonly opacity: number;
  readonly transparent: boolean;
  readonly depthWrite: boolean;
}

/** The materials of a model that can fade. */
export function fadeSlots(model: Object3D): FadeSlot[] {
  const slots: FadeSlot[] = [];
  const seen = new Set<Material>();
  model.traverse((obj) => {
    const material = (obj as Mesh).material as Material | Material[] | undefined;
    if (material === undefined) return;
    for (const m of Array.isArray(material) ? material : [material]) {
      if (seen.has(m)) continue;
      seen.add(m);
      slots.push({
        material: m,
        opacity: m.opacity,
        transparent: m.transparent,
        depthWrite: m.depthWrite,
      });
    }
  });
  return slots;
}

/**
 * Set the opacity of the model to `alpha` times its start opacity. At 1 the start values come
 * back, so a solid boss draws as before.
 */
export function applyFade(slots: readonly FadeSlot[], alpha: number): void {
  const solid = alpha >= 1;
  for (const s of slots) {
    const m = s.material;
    const transparent = solid ? s.transparent : true;
    if (m.transparent !== transparent) m.needsUpdate = true;
    m.transparent = transparent;
    m.depthWrite = solid ? s.depthWrite : false;
    m.opacity = s.opacity * alpha;
  }
}
